import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { getRequestApprovalFlow, submitRequestApprovalAction } from "../services/requestApprovalApi";
import { SendApprovalWorkflowEmailAsync } from "../services/sendMailSMTP";

const PRIMARY = "rgb(18, 76, 114)";

/**
 * Table-driven hiring-request approval flow.
 *
 * Renders the approval stepper for a hiring request AND handles the approve/reject
 * actions inline (so the legacy hardcoded gate functions in RequestDetails.js are no
 * longer needed). Steps, order, roles and per-type applicability all come from the
 * backend (RequestApprovalLevels / RequestApprovalProgress) — nothing is hardcoded here.
 *
 * If the backend returns no steps for this request, the whole component renders null,
 * which is exactly how "this request does not need approval" is expressed.
 *
 * Props:
 *   requestId        - hiring request id
 *   userRole         - current user's role name (e.g. "Finance Manager")
 *   userName         - current user's display name (recorded as the approver)
 *   requestStatus    - current request status ("Submitted" | "Approved" | ...)
 *   request          - the hiring request object (for the workflow-email payload)
 *   onActionComplete - callback fired after a successful approve/reject (refresh parent)
 */
const RequestApprovalFlow = ({ requestId, userRole, userName, requestStatus, request, onActionComplete }) => {
  const [flow, setFlow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { step, action: "Approved" | "Rejected" }
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadFlow = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      const data = await getRequestApprovalFlow(requestId);
      setFlow(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load request approval flow", err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadFlow();
  }, [loadFlow]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4">
        <div className="w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: "rgba(18,76,114,0.15)", borderTopColor: PRIMARY }} />
        <span className="text-sm" style={{ color: "rgba(18,76,114,0.5)" }}>Loading approval flow...</span>
      </div>
    );
  }

  // No configured levels for this request => no approval required.
  if (!flow.length) return null;

  // The current actionable step: first non-approved step whose predecessors are all approved.
  const isDone = (s) => s.approval_status === "Approved";
  const isBlocked = (s) => s.approval_status === "Rejected" || s.approval_status === "Send Back";
  const currentIndex = flow.findIndex((s) => !isDone(s));
  const currentStep = currentIndex === -1 ? null : flow[currentIndex];

  // Can the logged-in user act on the current step?
  const roleMatches = (step) =>
    step &&
    step.role_name &&
    userRole &&
    step.role_name.trim().toLowerCase() === userRole.trim().toLowerCase();
  const canActOnCurrent =
    !!currentStep &&
    !isBlocked(currentStep) &&
    roleMatches(currentStep) &&
    (requestStatus === "Submitted" || requestStatus === "Approved");

  const openModal = (action) => {
    setNotes("");
    setModal({ step: currentStep, action });
  };

  // Notify the next approver (or the business owner) after a decision. Routed through
  // the gated SendApprovalWorkflowEmailAsync so it also respects the email on/off toggle.
  const sendWorkflowEmail = async (action, actedStep, notesText, flowNow) => {
    if (!request) return;
    const base = {
      companyCode: request.companyCode || "",
      hiringManagerUserId: Number(request.hiring_manager) || null,
      requestId: request.request_id,
      referenceNumber: request.referenceNumber || "",
      actorName: userName,
      actorRole: userRole || "",
      notes: notesText || "",
      department: request.department_name || "",
      position: request.position || "",
      requestType: request.request_type || "",
    };
    try {
      if (action === "Approved") {
        const nextPending = (flowNow || []).find((s) => s.approval_status !== "Approved");
        if (nextPending) {
          // Hand off to the next approver in the chain.
          await SendApprovalWorkflowEmailAsync({
            ...base,
            recipientRole: nextPending.role_name,
            action: `${actedStep.level_name} Approved`,
            nextStep: `Pending ${nextPending.level_name} Approval`,
          });
        } else {
          // Final approval — notify the business owner (empty role => BO fallback).
          await SendApprovalWorkflowEmailAsync({
            ...base,
            recipientRole: "",
            action: "Approved",
            nextStep: "Ready for Convert to Sourcing",
          });
        }
      } else {
        // Rejected / Send Back — notify the business owner.
        await SendApprovalWorkflowEmailAsync({
          ...base,
          recipientRole: "",
          action: `${actedStep.level_name} Rejected`,
          nextStep: "Request Rejected",
        });
      }
    } catch (err) {
      console.error("Failed to send workflow email", err);
    }
  };

  const handleSubmit = async () => {
    if (!notes.trim()) {
      toast.error("Please enter notes.");
      return;
    }
    const actionNotes = notes.trim();
    const actedStep = modal.step;
    const action = modal.action;
    try {
      setSubmitting(true);
      await submitRequestApprovalAction({
        request_id: requestId,
        level_order: actedStep.level_order,
        action,
        approver_name: userName,
        notes: actionNotes,
      });
      toast.success(action === "Approved" ? "Approval recorded." : "Request rejected.");
      setModal(null);
      setNotes("");
      const newFlow = await getRequestApprovalFlow(requestId);
      setFlow(Array.isArray(newFlow) ? newFlow : []);
      await sendWorkflowEmail(action, actedStep, actionNotes, newFlow);
      if (onActionComplete) onActionComplete();
    } catch (err) {
      console.error("Failed to submit approval action", err);
      toast.error("Failed to record the decision.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-2 mt-4 mb-2 p-3 rounded-lg overflow-x-auto"
      style={{ backgroundColor: "rgba(18, 76, 114, 0.03)", border: "1px solid rgba(18, 76, 114, 0.08)" }}
    >
      {/* Stepper */}
      <div className="flex items-center gap-2">
        {flow.map((step, idx) => {
          const approved = isDone(step);
          const rejected = isBlocked(step);
          const current = !approved && !rejected && idx === currentIndex;
          const color = approved ? "#059669" : rejected ? "#ef4444" : current ? "#d97706" : "#9ca3af";
          const isLast = idx === flow.length - 1;
          const approverName = step.approver_name || "";
          return (
            <React.Fragment key={idx}>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className="flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                  style={{ backgroundColor: color, color: "#fff" }}
                >
                  {approved ? "✓" : rejected ? "✗" : step.level_order}
                </div>
                <div>
                  <div className="text-xs font-semibold whitespace-nowrap" style={{ color }}>
                    {step.level_name}
                  </div>
                  <div className="text-[10px] whitespace-nowrap" style={{ color: approved ? "#059669" : "#6b7280" }}>
                    {approved
                      ? `${approverName || "Approved"}${step.approved_date ? " · " + new Date(step.approved_date).toLocaleDateString() : ""}`
                      : rejected
                        ? approverName || step.approval_status
                        : current
                          ? `⏳ ${approverName || "Pending"}`
                          : approverName || "Waiting"}
                  </div>
                </div>
              </div>
              {!isLast && (
                <div
                  className="flex-shrink-0 w-8 h-0.5 rounded"
                  style={{ backgroundColor: approved ? "#059669" : "#e5e7eb" }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Inline action buttons for the current approver */}
      {canActOnCurrent && (
        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => openModal("Approved")}
            className="bg-emerald-600 text-white px-4 py-1.5 rounded-md hover:bg-emerald-700 transition text-sm font-medium"
          >
            {currentStep.level_name} Approve
          </button>
          <button
            onClick={() => openModal("Rejected")}
            className="bg-rose-600 text-white px-4 py-1.5 rounded-md hover:bg-rose-700 transition text-sm font-medium"
          >
            {currentStep.level_name} Reject
          </button>
        </div>
      )}

      {/* Notes modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-1" style={{ color: PRIMARY }}>
              {modal.action === "Approved" ? "Approve" : "Reject"} — {modal.step.level_name}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {modal.action === "Approved"
                ? `Confirm ${modal.step.level_name} approval for this request.`
                : `Reject this request at the ${modal.step.level_name} step.`}
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="Enter notes..."
              className="w-full border rounded-lg p-2 text-sm outline-none"
              style={{ borderColor: "rgba(18,76,114,0.3)" }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setModal(null); setNotes(""); }}
                disabled={submitting}
                className="px-4 py-2 rounded-md border text-sm"
                style={{ color: PRIMARY, borderColor: "rgba(18,76,114,0.3)" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className={`px-4 py-2 rounded-md text-white text-sm ${modal.action === "Approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {submitting ? "Saving..." : modal.action === "Approved" ? "Confirm Approval" : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestApprovalFlow;
